import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    ActionButton,
    AlertDialog,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Form,
    Heading,
    Item,
    Menu,
    MenuTrigger,
    ProgressCircle,
    TextField,
} from '@adobe/react-spectrum';
import { toast } from 'react-toastify';
import './LinkedAccounts.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { BreadCrumbProfileNavBar } from '../breadcrumbs/buttons/BreadCrumbProfileNavBar';
import {
    deleteAccountLink,
    getAccountLinkCode,
    postValidateAccountLink,
    resetAddProfileDialog,
} from '../../data/redux/slices/profileSlice';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import HeroPanel, { HeroPanelChip, HeroPanelTotal } from '../common/heroPanel/HeroPanel';
import { LINK_PROVIDERS, getLinkProvider, getLinkProviderLabel } from '../../utils/linkedAccountProviders';

const SOURCE_LIST = LINK_PROVIDERS.map(({ label }) => ({ id: label, name: label }));

export function LinkedAccounts() {
    const dispatch = useDispatch();

    const isFetching = useSelector((state) => state?.profile?.isFetching);
    const isFetchingIdentifierString = useSelector((state) => state?.profile?.accountLink?.isFetchingIdentifierString);
    const identifierString = useSelector((state) => state?.profile?.accountLink?.identifierString);
    const validationSuccessful = useSelector((state) => state?.profile?.accountLink?.validationSuccessful);
    const profiles = useSelector((state) => state?.profile?.profiles);
    const isValidating = useSelector((state) => state?.profile?.accountLink?.isValidating);

    const helmetName = `Linked Accounts`;
    const helmetCanonical = `https://www.commandersalt.com/linkedaccounts`;
    const helmetDescription = `The saltiest MtG deck builders on the planet!`;
    const helmetTitle = `Commandersalt.com - The saltiest MtG deck builders on the planet!`;

    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [linkSource, setLinkSource] = useState('Select');
    const [dialogFormValueValid, setDialogFormValueValid] = useState(undefined);
    const [username, setUsername] = useState('');
    const [hasUpdatedProfile, setHasUpdatedProfile] = useState(false);
    const [hasPressedValidate, setHasPressedValidate] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [confirmRemoveRow, setConfirmRemoveRow] = useState(null);
    const [usernameError, setUsernameError] = useState(null);

    const activeProvider = getLinkProvider(linkSource);

    const rows = useMemo(() => {
        const out = [];
        let iterator = 0;
        Object.keys(profiles || {}).forEach((profileProvider) => {
            Object.keys(profiles[profileProvider] || {}).forEach((profileName) => {
                out.push({
                    id: iterator,
                    key: iterator,
                    username: profileName,
                    provider: profileProvider,
                });
                iterator += 1;
            });
        });
        return out;
    }, [profiles]);

    const total = rows.length;
    const byProviderCount = useMemo(() => {
        const counts = {};
        rows.forEach((r) => {
            const lbl = getLinkProviderLabel(r.provider);
            counts[lbl] = (counts[lbl] || 0) + 1;
        });
        return counts;
    }, [rows]);

    const validateLinkCode = async () => {
        dispatch(postValidateAccountLink(linkSource.toLowerCase(), username.trim()));
        setHasPressedValidate(true);
    };

    const handleFormChange = (text) => {
        const handle = (text || '').trim();
        if (!handle) {
            setDialogFormValueValid(undefined);
            setUsernameError(null);
            return;
        }
        // Don't gate on a character set by default. Moxfield / Archidekt / TappedOut
        // handles differ widely — TappedOut in particular allows spaces and punctuation —
        // so any allow/deny list wrongly blocks valid profiles (an earlier list
        // rejected `_`, then this one still rejected spaces/symbols). The backend
        // bio-check is the source of truth for whether the profile exists; the
        // handle is URL-encoded before it's sent (DynamoConnector.postValidateLinkCode).
        // The exception is a provider that publishes its own signup rule
        // (LINK_PROVIDERS[].usernamePattern) — there the API rejects a malformed handle
        // with an error that reads as a missing bio code, so catch it here instead.
        if (activeProvider?.usernamePattern && !activeProvider.usernamePattern.test(handle)) {
            setDialogFormValueValid('invalid');
            setUsernameError(activeProvider.usernameHint);
            return;
        }
        // We also reject a handle already linked to this account.
        const alreadyLinked = rows.some((row) => row.username === handle);
        setDialogFormValueValid(alreadyLinked ? 'invalid' : 'valid');
        setUsernameError(alreadyLinked ? 'That profile is already linked to your account.' : null);
    };

    const openAddDialog = () => {
        dispatch(resetAddProfileDialog());
        setLinkSource('Select');
        setUsername('');
        setHasUpdatedProfile(false);
        setHasPressedValidate(false);
        setDialogFormValueValid(undefined);
        setUsernameError(null);
        setShowLinkDialog(true);
    };

    const closeAddDialog = () => {
        setShowLinkDialog(false);
    };

    const deleteSelected = () => {
        const row = confirmRemoveRow;
        if (!row) return;
        dispatch(deleteAccountLink([{ username: row.username, provider: row.provider }]));
        setSelectedKey(null);
        setConfirmRemoveRow(null);
    };

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs content={<BreadCrumbProfileNavBar />} />
            {isFetching && <GenericLoadingDialog />}

            <DialogContainer onDismiss={() => setConfirmRemoveRow(null)}>
                {confirmRemoveRow && (
                    <AlertDialog
                        variant="destructive"
                        title="Remove connection"
                        primaryActionLabel="Remove"
                        cancelLabel="Cancel"
                        onPrimaryAction={deleteSelected}
                        onCancel={() => {
                            setSelectedKey(null);
                            setConfirmRemoveRow(null);
                        }}
                    >
                        Disconnect <strong>@{confirmRemoveRow.username}</strong> on{' '}
                        {getLinkProviderLabel(confirmRemoveRow.provider)}? Decks imported from that profile will no
                        longer be attributed to your Commandersalt account.
                    </AlertDialog>
                )}
            </DialogContainer>

            {showLinkDialog && (
                <DialogContainer type="modal" isDismissable onDismiss={closeAddDialog}>
                    <Dialog UNSAFE_className="linkedAccountsDialog" maxWidth="calc(100vw - 20px)" width={620}>
                        <Heading UNSAFE_className="linkedAccountsDialogHead">
                            <span className="linkedAccountsDialogEyebrow">Add Connection</span>
                            <span className="linkedAccountsDialogTitle">Link a deckbuilder profile</span>
                        </Heading>
                        <Content UNSAFE_className="linkedAccountsDialogContent">
                            <p className="linkedAccountsDialogHint">
                                A short link code lets us prove you own the profile. Once verified, decks
                                imported to Commandersalt from that profile will be attributed to your
                                account.
                            </p>
                            <ol className="linkedAccountsSteps">
                                <li
                                    className={`linkedAccountsStep${
                                        linkSource && linkSource !== 'Select'
                                            ? ' is-complete'
                                            : ' is-current'
                                    }`}
                                >
                                    <span className="linkedAccountsStepIndex">1</span>
                                    <div className="linkedAccountsStepBody">
                                        <span className="linkedAccountsStepLabel">Pick the source</span>
                                        <span className="linkedAccountsStepHint">
                                            Which site hosts the profile you want to link?
                                        </span>
                                        <MenuTrigger>
                                            <ActionButton
                                                isDisabled={validationSuccessful}
                                                UNSAFE_className={`linkedAccountsSourceBtn${
                                                    linkSource && linkSource !== 'Select'
                                                        ? ' is-selected'
                                                        : ''
                                                }`}
                                            >
                                                <span className="linkedAccountsSourceBtnLabel">
                                                    {linkSource === 'Select' ? 'Select source…' : linkSource}
                                                </span>
                                                <span className="linkedAccountsSourceBtnCaret" aria-hidden>
                                                    ▾
                                                </span>
                                            </ActionButton>
                                            <Menu
                                                items={SOURCE_LIST}
                                                selectedKeys={linkSource}
                                                onSelectionChange={(evn) => {
                                                    const next = evn?.currentKey;
                                                    if (!next) return;
                                                    setLinkSource(next);
                                                    // The handle rule travels with the provider, so a
                                                    // handle typed for the previous one can't carry over.
                                                    setUsername('');
                                                    setDialogFormValueValid(undefined);
                                                    setUsernameError(null);
                                                    dispatch(getAccountLinkCode(next.toLowerCase()));
                                                }}
                                                selectionMode="single"
                                            >
                                                {(item) => <Item>{item.name}</Item>}
                                            </Menu>
                                        </MenuTrigger>
                                    </div>
                                </li>

                                {(isFetchingIdentifierString ||
                                    (identifierString && linkSource !== 'Select')) && (
                                    <li
                                        className={`linkedAccountsStep${
                                            hasUpdatedProfile ? ' is-complete' : ' is-current'
                                        }`}
                                    >
                                        <span className="linkedAccountsStepIndex">2</span>
                                        <div className="linkedAccountsStepBody">
                                            <span className="linkedAccountsStepLabel">
                                                Drop the link code in your bio
                                            </span>
                                            <span className="linkedAccountsStepHint">
                                                {activeProvider?.bioField ? (
                                                    <>
                                                        Paste this into the <strong>{activeProvider.bioField}</strong>{' '}
                                                        field in your{' '}
                                                        {activeProvider.settingsUrl ? (
                                                            <a
                                                                href={activeProvider.settingsUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                {linkSource} profile settings
                                                            </a>
                                                        ) : (
                                                            `${linkSource} profile settings`
                                                        )}
                                                        , then save.
                                                    </>
                                                ) : (
                                                    <>Paste this anywhere in your public {linkSource} profile bio.</>
                                                )}{' '}
                                                It's temporary — you can remove it after we verify.
                                            </span>
                                            {isFetchingIdentifierString && !identifierString ? (
                                                <div className="linkedAccountsLoading">
                                                    <ProgressCircle isIndeterminate size="S" aria-label="Fetching code" />
                                                    <span>Pulling your code…</span>
                                                </div>
                                            ) : identifierString ? (
                                                <div className="linkedAccountsCodeBlock">
                                                    <code className="linkedAccountsCode">{identifierString}</code>
                                                    <button
                                                        type="button"
                                                        className="linkedAccountsCopyBtn"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard?.writeText(
                                                                    identifierString
                                                                );
                                                                toast('Link code copied!');
                                                            } catch {
                                                                toast.error('Could not copy — copy it manually.');
                                                            }
                                                        }}
                                                        aria-label="Copy link code"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            ) : null}
                                            {identifierString && (
                                                <button
                                                    type="button"
                                                    className="searedPrimaryBtn"
                                                    onClick={() => setHasUpdatedProfile(true)}
                                                    disabled={validationSuccessful}
                                                >
                                                    I've updated my profile
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                )}

                                {hasUpdatedProfile && (
                                    <li
                                        className={`linkedAccountsStep${
                                            validationSuccessful ? ' is-complete' : ' is-current'
                                        }`}
                                    >
                                        <span className="linkedAccountsStepIndex">3</span>
                                        <div className="linkedAccountsStepBody">
                                            <span className="linkedAccountsStepLabel">
                                                Tell us your {linkSource} username
                                            </span>
                                            <span className="linkedAccountsStepHint">
                                                We fetch your bio and verify the code matches.
                                            </span>
                                            <Form validationBehavior="native">
                                                <TextField
                                                    isDisabled={validationSuccessful}
                                                    onChange={(val) => {
                                                        setUsername(val);
                                                        handleFormChange(val);
                                                    }}
                                                    value={username || ''}
                                                    width="100%"
                                                    isRequired
                                                    onSubmit={validateLinkCode}
                                                    autoFocus={false}
                                                    label={`${linkSource} username`}
                                                    validationState={dialogFormValueValid}
                                                    errorMessage={usernameError || undefined}
                                                    description={
                                                        !usernameError ? activeProvider?.usernameHint : undefined
                                                    }
                                                />
                                                {!isValidating && hasPressedValidate && (
                                                    <div
                                                        className={`linkedAccountsValidationResult linkedAccountsValidationResult--${
                                                            validationSuccessful ? 'ok' : 'fail'
                                                        }`}
                                                    >
                                                        <span className="linkedAccountsValidationIcon" aria-hidden>
                                                            {validationSuccessful ? '✓' : '✕'}
                                                        </span>
                                                        <span className="linkedAccountsValidationText">
                                                            {validationSuccessful
                                                                ? 'Verified — connection saved.'
                                                                : "Couldn't verify. Double-check the username and that the link code is in your bio."}
                                                        </span>
                                                    </div>
                                                )}
                                                {isValidating ? (
                                                    <div className="linkedAccountsLoading">
                                                        <ProgressCircle
                                                            isIndeterminate
                                                            size="S"
                                                            aria-label="Validating"
                                                        />
                                                        <span>Verifying…</span>
                                                    </div>
                                                ) : (
                                                    !validationSuccessful && (
                                                        <button
                                                            type="button"
                                                            className="searedPrimaryBtn"
                                                            onClick={validateLinkCode}
                                                            disabled={dialogFormValueValid !== 'valid'}
                                                        >
                                                            Validate
                                                        </button>
                                                    )
                                                )}
                                            </Form>
                                        </div>
                                    </li>
                                )}

                                {validationSuccessful && (
                                    <li className="linkedAccountsStep is-final">
                                        <span className="linkedAccountsStepIndex">✓</span>
                                        <div className="linkedAccountsStepBody">
                                            <span className="linkedAccountsStepLabel">
                                                Account linked!
                                            </span>
                                            <span className="linkedAccountsStepHint">
                                                You can remove the link code from your {linkSource} bio whenever
                                                — it's no longer needed.
                                            </span>
                                            <button
                                                type="button"
                                                className="searedPrimaryBtn"
                                                onClick={closeAddDialog}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </li>
                                )}
                            </ol>
                        </Content>
                    </Dialog>
                </DialogContainer>
            )}

            <div className="linkedAccountsPage">
                <HeroPanel
                    tone="purple"
                    className="linkedAccountsHero"
                    eyebrow="Linked Accounts"
                    title="Claim decks from your other profiles"
                    hint="Verify ownership of your Moxfield, Archidekt, TappedOut, or MythicHub profile so any deck imported to Commandersalt from that profile is attributed to your account."
                    aside={
                        <>
                            <HeroPanelTotal label="Linked" value={total.toLocaleString()} />
                            {Object.entries(byProviderCount).map(([label, count]) => (
                                <HeroPanelChip
                                    key={label}
                                    accent="cool"
                                    label={label}
                                    value={count.toLocaleString()}
                                />
                            ))}
                        </>
                    }
                />

                <div className="linkedAccountsActionRow">
                    <p className="linkedAccountsActionHint">
                        {total === 0
                            ? "You haven't linked any deckbuilder profiles yet."
                            : `${total} ${total === 1 ? 'profile' : 'profiles'} attributing decks to your account.`}
                    </p>
                    <button
                        type="button"
                        className="searedPrimaryBtn searedPrimaryBtn--lg"
                        onClick={openAddDialog}
                    >
                        <span className="linkedAccountsBtnPlus" aria-hidden>
                            +
                        </span>
                        Add account
                    </button>
                </div>

                <div className="linkedAccountsList" role="list">
                    {rows.length === 0 ? (
                        <div className="linkedAccountsEmpty">
                            <span className="linkedAccountsEmptyIcon" aria-hidden>
                                ⌁
                            </span>
                            <p className="linkedAccountsEmptyTitle">No connected accounts yet</p>
                            <p className="linkedAccountsEmptyHint">
                                Click <strong>Add account</strong> to wire up your first deckbuilder profile.
                            </p>
                        </div>
                    ) : (
                        rows.map((row) => {
                            const isSelected = selectedKey === row.id;
                            const provider = getLinkProviderLabel(row.provider);
                            return (
                                <article
                                    key={row.id}
                                    className={`linkedAccountsRow${
                                        isSelected ? ' is-selected' : ''
                                    } linkedAccountsRow--${row.provider.toLowerCase()}`}
                                    role="listitem"
                                >
                                    <div className="linkedAccountsRowMain">
                                        <span className="linkedAccountsRowProvider">{provider}</span>
                                        <span className="linkedAccountsRowName">@{row.username}</span>
                                    </div>
                                    <div className="linkedAccountsRowActions">
                                        <button
                                            type="button"
                                            className="linkedAccountsBtn linkedAccountsBtn--danger"
                                            onClick={() => {
                                                setSelectedKey(row.id);
                                                setConfirmRemoveRow(row);
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>
            </div>
        </Flex>
    );
}
